import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FIGHTERS, TICKER_TAPE } from "@/data/fighters";
import CoinAvatar from "@/components/CoinAvatar";
import HPBar from "@/components/HPBar";
import ComicWord from "@/components/ComicWord";
import {
  loadScorecard,
  recordResult,
  rollDamage,
  randomCommentary,
  tauntLine,
  winLine,
  favoriteFighter,
  defaultScorecard,
} from "@/lib/battle";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import FighterBody from "@/components/FighterBody";
import { Swords, Trophy, Zap, Heart, Volume2, VolumeX, RotateCcw, Flame, Shield } from "lucide-react";

// ---------- Audio (synthetic, Web Audio API) ----------
const audioCtxRef = { current: null };
const getCtx = () => {
  if (!audioCtxRef.current) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) audioCtxRef.current = new AC();
  }
  return audioCtxRef.current;
};
const beep = (freq = 440, dur = 0.12, type = "square", gain = 0.12) => {
  const ctx = getCtx();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.value = gain;
  osc.connect(g).connect(ctx.destination);
  osc.start();
  osc.frequency.exponentialRampToValueAtTime(freq * 0.3, ctx.currentTime + dur);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
  osc.stop(ctx.currentTime + dur);
};
const SFX = {
  punch: () => beep(180, 0.12, "square", 0.18),
  special: () => {
    beep(620, 0.08, "sawtooth", 0.14);
    setTimeout(() => beep(320, 0.18, "square", 0.18), 70);
    setTimeout(() => beep(140, 0.22, "sawtooth", 0.2), 180);
  },
  heal: () => {
    beep(880, 0.08, "sine", 0.1);
    setTimeout(() => beep(1320, 0.14, "sine", 0.1), 80);
  },
  taunt: () => beep(240, 0.14, "triangle", 0.1),
  ko: () => {
    [600, 480, 360, 220, 120].forEach((f, i) =>
      setTimeout(() => beep(f, 0.14, "sawtooth", 0.18), i * 90)
    );
  },
  select: () => beep(520, 0.06, "triangle", 0.08),
};

// ---------- Floating damage number ----------
const DamagePop = ({ pop }) => (
  <div
    className="absolute left-1/2 top-1/3 -translate-x-1/2 anim-float-up pointer-events-none z-30"
    style={{ textShadow: "3px 3px 0 #000" }}
  >
    <span
      className={`font-display text-4xl sm:text-5xl ${pop.crit ? "text-yellow-300" : "text-white"}`}
      style={{ WebkitTextStroke: "2px #000" }}
    >
      {pop.text}
    </span>
  </div>
);

// ---------- Fighter card (roster) ----------
const FighterCard = ({ fighter, selected, disabled, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={`fighter-card-${fighter.id}`}
    className={`relative text-left p-4 border-4 border-black cb-shadow cb-hover rounded-xl w-full
      ${selected ? "ring-4 ring-offset-2 ring-offset-[#fffdf0] ring-black -rotate-1" : ""}
      ${disabled ? "opacity-40 cursor-not-allowed cb-shadow" : "cursor-pointer"}`}
    style={{ background: fighter.color }}
  >
    {selected && (
      <span
        className="absolute -top-3 -right-3 bg-black text-white font-display text-xs px-3 py-1 rotate-6 border-2 border-black"
        data-testid={`fighter-selected-badge-${fighter.id}`}
      >
        {label}
      </span>
    )}
    <div className="flex items-center gap-4">
      <CoinAvatar fighter={fighter} size={76} />
      <div className="flex-1 min-w-0">
        <div className="font-display text-xl sm:text-2xl leading-none">{fighter.name}</div>
        <div className="font-marker text-sm opacity-80">{fighter.title}</div>
        <div className="font-display text-xs mt-1 bg-black text-white inline-block px-2 py-0.5">
          {fighter.ticker}
        </div>
      </div>
    </div>
    <div className="mt-3 grid grid-cols-4 gap-1 text-center font-display text-[11px]">
      <StatPip label="HP" value={fighter.stats.hp} bg="#fff" />
      <StatPip label="ATK" value={fighter.stats.atk} bg="#fff" />
      <StatPip label="DEF" value={fighter.stats.def} bg="#fff" />
      <StatPip label="SPD" value={fighter.stats.speed} bg="#fff" />
    </div>
  </button>
);

const StatPip = ({ label, value, bg = "#FFDF00" }) => (
  <div
    className="border-2 border-black py-1 px-1"
    style={{ background: bg }}
    data-testid={`stat-${label.toLowerCase()}`}
  >
    <div className="text-[9px] tracking-widest">{label}</div>
    <div className="text-sm">{value}</div>
  </div>
);

// ---------- Fighter arena display (big) ----------
const FighterArena = ({ fighter, side = "left", hp, maxHp, shake, slam, pop, isDead, label }) => (
  <div
    className={`relative p-4 sm:p-6 border-4 border-black rounded-xl cb-shadow-lg overflow-hidden`}
    style={{ background: side === "left" ? "#FFF2A6" : "#FFD2E6" }}
    data-testid={`arena-side-${side}`}
  >
    <div className="halftone-light absolute inset-0 opacity-30 pointer-events-none" />
    <div className="relative flex items-center justify-between gap-2 mb-3">
      <div>
        <div className="font-display text-xl sm:text-2xl leading-none">{fighter.name}</div>
        <div className="font-marker text-xs sm:text-sm">{fighter.title}</div>
      </div>
      <span
        className="font-display text-[10px] sm:text-xs px-2 py-1 border-2 border-black bg-black text-white"
        data-testid={`arena-label-${side}`}
      >
        {label}
      </span>
    </div>

    <div className="relative flex items-center justify-center my-2 h-[180px] sm:h-[220px]">
      <div
        className={`relative ${shake ? "anim-shake" : "anim-bob"} ${slam ? "anim-slam" : ""}`}
        style={{ filter: isDead ? "grayscale(1) brightness(0.6)" : "none" }}
      >
        <CoinAvatar fighter={fighter} size={180} />
        {isDead && (
          <div className="absolute inset-0 flex items-center justify-center">
            <ComicWord word="K.O." color="#FF1744" rotate={-10} />
          </div>
        )}
      </div>
      {pop && <DamagePop pop={pop} />}
    </div>

    <HPBar
      current={hp}
      max={maxHp}
      label={fighter.ticker}
      testId={`hp-bar-${side}`}
    />
    <div className="grid grid-cols-4 gap-1 mt-3">
      <StatPip label="HP" value={maxHp} bg="#fff" />
      <StatPip label="ATK" value={fighter.stats.atk} bg="#fff" />
      <StatPip label="DEF" value={fighter.stats.def} bg="#fff" />
      <StatPip label="SPD" value={fighter.stats.speed} bg="#fff" />
    </div>
  </div>
);

// ---------- Ticker tape ----------
const Ticker = () => {
  const items = [...TICKER_TAPE, ...TICKER_TAPE];
  return (
    <div className="w-full bg-black text-white border-y-4 border-black overflow-hidden">
      <div className="anim-ticker flex whitespace-nowrap py-2">
        {items.map((t, i) => (
          <span key={i} className="font-display text-sm sm:text-base px-6">
            <span className="text-[#39FF14]">★</span> {t}
          </span>
        ))}
      </div>
    </div>
  );
};

// ---------- Main component ----------
export default function Home() {
  const [phase, setPhase] = useState("home"); // home | select | battle | result
  const [mode, setMode] = useState("turn"); // turn | auto
  const [playerId, setPlayerId] = useState(null);
  const [oppId, setOppId] = useState(null);
  const [sound, setSound] = useState(true);
  const [showScorecard, setShowScorecard] = useState(false);
  const [sc, setSc] = useState(() => loadScorecard());
  const [lastResult, setLastResult] = useState({ won: false });

  const sfx = useCallback(
    (name) => {
      if (!sound) return;
      SFX[name]?.();
    },
    [sound]
  );

  const player = useMemo(() => FIGHTERS.find((f) => f.id === playerId), [playerId]);
  const opp = useMemo(() => FIGHTERS.find((f) => f.id === oppId), [oppId]);

  const startBattle = (m) => {
    if (!player || !opp) return;
    setMode(m);
    setPhase("battle");
  };

  const resetAll = () => {
    setPlayerId(null);
    setOppId(null);
    setPhase("home");
  };

  const resetScorecard = () => {
    const fresh = defaultScorecard();
    setSc(fresh);
    localStorage.removeItem("meme-brawl-scorecard-v1");
  };

  return (
    <div className="App paper-bg min-h-screen">
      <TopBar
        sound={sound}
        onToggleSound={() => setSound((s) => !s)}
        onOpenScore={() => setShowScorecard(true)}
        onHome={resetAll}
        sc={sc}
      />
      <Ticker />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {phase === "home" && (
          <Landing onStart={() => setPhase("select")} fighters={FIGHTERS} />
        )}
        {phase === "select" && (
          <Selection
            playerId={playerId}
            oppId={oppId}
            setPlayerId={(id) => {
              sfx("select");
              setPlayerId(id);
              if (id === oppId) setOppId(null);
            }}
            setOppId={(id) => {
              sfx("select");
              setOppId(id);
            }}
            onFight={startBattle}
            onBack={() => setPhase("home")}
          />
        )}
        {phase === "battle" && player && opp && (
          <Battle
            key={`${player.id}-${opp.id}-${mode}`}
            player={player}
            opp={opp}
            mode={mode}
            sfx={sfx}
            onEnd={(won) => {
              const next = recordResult(sc, { playerId: player.id, won });
              setSc(next);
              setPhase("result");
              setLastResult({ won });
            }}
          />
        )}
        {phase === "result" && player && opp && (
          <ResultScreen
            won={lastResult.won}
            player={player}
            opp={opp}
            onRematch={() => setPhase("battle")}
            onNewFighters={() => setPhase("select")}
            onHome={resetAll}
          />
        )}
      </main>

      <ScorecardDialog
        open={showScorecard}
        onOpenChange={setShowScorecard}
        sc={sc}
        onReset={resetScorecard}
      />
      <Footer />
    </div>
  );
}

function TopBar({ sound, onToggleSound, onOpenScore, onHome, sc }) {
  return (
    <header className="sticky top-0 z-40 border-b-4 border-black bg-[#FFEB3B]">
      <div className="halftone-light absolute inset-0 opacity-30 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <button
          onClick={onHome}
          className="flex items-center gap-3 cursor-pointer"
          data-testid="home-logo-btn"
        >
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-[#FF2A85] border-4 border-black flex items-center justify-center font-display text-white">
              M
            </div>
          </div>
          <div className="leading-none text-left">
            <div className="font-display text-xl sm:text-2xl">MEME BRAWL</div>
            <div className="font-marker text-xs">// coin fights, zero fees</div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={onOpenScore}
            className="font-display text-xs sm:text-sm bg-white border-4 border-black px-3 py-2 cb-shadow-sm cb-hover flex items-center gap-2"
            data-testid="open-scorecard-btn"
          >
            <Trophy className="w-4 h-4" />
            <span className="hidden sm:inline">SCORECARD</span>
            <span className="bg-black text-white px-2 py-0.5 text-xs" data-testid="scorecard-wins-count">
              {sc.wins}W / {sc.losses}L
            </span>
          </button>
          <button
            onClick={onToggleSound}
            className="font-display text-xs sm:text-sm bg-white border-4 border-black px-3 py-2 cb-shadow-sm cb-hover"
            data-testid="toggle-sound-btn"
            aria-label="Toggle sound"
          >
            {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-10 border-t-4 border-black bg-black text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="font-display text-sm">
          MEME BRAWL &mdash; <span className="text-[#39FF14]">NOT FINANCIAL ADVICE</span>, JUST FINANCIAL FISTS
        </p>
        <p className="font-marker text-sm text-neutral-300">v1 • cartoon crypto chaos</p>
      </div>
    </footer>
  );
}

// ---------- Landing ----------
const Landing = ({ onStart, fighters }) => {
  const spot = fighters[0];
  const spot2 = fighters[2];
  return (
    <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-testid="landing-section">
      <div className="lg:col-span-7 relative">
        <div className="relative bg-[#FFDF00] border-4 border-black cb-shadow-lg p-6 sm:p-10 rounded-2xl overflow-hidden">
          <div className="halftone-light absolute inset-0 opacity-30 pointer-events-none" />
          <div className="relative">
            <div className="inline-block mb-4">
              <ComicWord word="BRAWL!" color="#FF2A85" rotate={-6} />
            </div>
            <h1
              className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] uppercase"
              style={{ textShadow: "4px 4px 0 #000", WebkitTextStroke: "1px #000" }}
            >
              Make the <span className="text-[#39FF14]">meme coins</span> fight.
            </h1>
            <p className="font-marker text-lg sm:text-2xl mt-4 max-w-xl">
              Pick your champion. Pick a rival. Throw hands with <strong>$DOGE</strong>, <strong>$PEPE</strong>,{" "}
              <strong>$WIF</strong> and friends. No gas. All chaos.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onStart}
                className="font-display text-lg sm:text-xl bg-black text-white hover:bg-neutral-800 border-4 border-black px-6 py-6 cb-shadow cb-hover rounded-xl"
                data-testid="landing-start-btn"
              >
                <Swords className="mr-2" /> ENTER THE ARENA
              </Button>
              <a
                href="#roster"
                className="font-display text-lg sm:text-xl inline-flex items-center justify-center bg-white border-4 border-black px-6 py-4 cb-shadow cb-hover rounded-xl"
                data-testid="landing-roster-link"
              >
                MEET THE FIGHTERS
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-5 flex items-center justify-center relative min-h-[300px]">
        <div className="absolute -left-2 top-6 rotate-neg-6">
          <CoinAvatar fighter={spot} size={190} />
        </div>
        <div className="absolute right-0 bottom-0 rotate-pos-3 anim-bob">
          <CoinAvatar fighter={spot2} size={160} />
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <ComicWord word="VS" color="#00E5FF" rotate={-10} size="5xl" />
        </div>
      </div>

      <div id="roster" className="lg:col-span-12 mt-4">
        <h2 className="font-display text-2xl sm:text-4xl mb-4">
          THE ROSTER <span className="font-marker text-base">// 8 degens</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {fighters.map((f) => (
            <div
              key={f.id}
              className="p-4 border-4 border-black cb-shadow rounded-xl"
              style={{ background: f.color }}
              data-testid={`roster-tile-${f.id}`}
            >
              <div className="flex items-center gap-3">
                <CoinAvatar fighter={f} size={64} />
                <div>
                  <div className="font-display text-lg leading-none">{f.name}</div>
                  <div className="font-marker text-xs">{f.title}</div>
                </div>
              </div>
              <p className="font-body text-sm mt-2 bg-white border-2 border-black p-2 relative">
                <Flame className="inline w-3 h-3 mr-1" />
                <strong>{f.specialName}</strong>
                <span className="block text-xs opacity-70 mt-1">&ldquo;{f.quote}&rdquo;</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------- Selection ----------
const Selection = ({ playerId, oppId, setPlayerId, setOppId, onFight, onBack }) => {
  const [pickingFor, setPickingFor] = useState("player"); // player | opp
  const handlePick = (f) => {
    if (pickingFor === "player") {
      setPlayerId(f.id);
      if (!oppId) setPickingFor("opp");
    } else {
      if (f.id === playerId) return;
      setOppId(f.id);
    }
  };
  const player = FIGHTERS.find((f) => f.id === playerId);
  const opp = FIGHTERS.find((f) => f.id === oppId);
  const canFight = player && opp;

  return (
    <section data-testid="selection-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl sm:text-4xl">PICK YOUR FIGHTERS</h2>
        <button
          onClick={onBack}
          className="font-display text-xs sm:text-sm bg-white border-4 border-black px-3 py-2 cb-shadow-sm cb-hover"
          data-testid="selection-back-btn"
        >
          ← HOME
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mb-6">
        <SlotPanel
          title="YOU"
          color="#FFF2A6"
          fighter={player}
          active={pickingFor === "player"}
          onActivate={() => setPickingFor("player")}
          side="left"
        />
        <div className="lg:col-span-2 flex items-center justify-center min-h-[120px]">
          <ComicWord word="VS" color="#FF2A85" rotate={-6} />
        </div>
        <SlotPanel
          title="RIVAL"
          color="#FFD2E6"
          fighter={opp}
          active={pickingFor === "opp"}
          onActivate={() => setPickingFor("opp")}
          side="right"
        />
      </div>

      <p className="font-marker text-base mb-3">
        Tap <strong>{pickingFor === "player" ? "YOU" : "RIVAL"}</strong> slot to switch — then pick a coin below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-32">
        {FIGHTERS.map((f) => {
          const isPlayer = f.id === playerId;
          const isOpp = f.id === oppId;
          const label = isPlayer ? "YOU" : isOpp ? "RIVAL" : "";
          const disabled = pickingFor === "opp" && isPlayer;
          return (
            <FighterCard
              key={f.id}
              fighter={f}
              selected={isPlayer || isOpp}
              label={label}
              disabled={disabled}
              onClick={() => handlePick(f)}
            />
          );
        })}
      </div>

      <div className="sticky bottom-4 mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          onClick={() => onFight("turn")}
          disabled={!canFight}
          className="font-display text-lg sm:text-xl bg-[#39FF14] text-black hover:bg-[#39FF14] border-4 border-black px-6 py-6 cb-shadow cb-hover rounded-xl disabled:opacity-50"
          data-testid="start-turn-mode-btn"
        >
          <Swords className="mr-2" /> TURN-BASED BRAWL
        </Button>
        <Button
          onClick={() => onFight("auto")}
          disabled={!canFight}
          className="font-display text-lg sm:text-xl bg-[#00E5FF] text-black hover:bg-[#00E5FF] border-4 border-black px-6 py-6 cb-shadow cb-hover rounded-xl disabled:opacity-50"
          data-testid="start-auto-mode-btn"
        >
          <Zap className="mr-2" /> AUTO-BATTLER
        </Button>
      </div>
    </section>
  );
};

const SlotPanel = ({ title, color, fighter, active, onActivate, side }) => (
  <button
    type="button"
    onClick={onActivate}
    className={`lg:col-span-5 relative text-left p-4 sm:p-5 border-4 border-black rounded-xl cb-shadow w-full ${
      active ? "ring-4 ring-black ring-offset-2 ring-offset-[#fffdf0] -rotate-1" : ""
    }`}
    style={{ background: color }}
    data-testid={`slot-${side}`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="font-display text-lg sm:text-xl">{title}</span>
      {active && (
        <span className="font-display text-[10px] bg-black text-white px-2 py-1">PICKING</span>
      )}
    </div>
    <div className="flex items-center gap-4 min-h-[110px]">
      {fighter ? (
        <>
          <CoinAvatar fighter={fighter} size={100} />
          <div>
            <div className="font-display text-xl">{fighter.name}</div>
            <div className="font-marker text-sm">{fighter.title}</div>
            <div className="font-display text-xs mt-1 bg-black text-white inline-block px-2 py-0.5">
              {fighter.ticker}
            </div>
          </div>
        </>
      ) : (
        <div className="font-marker text-neutral-700 text-base">Empty slot — tap a coin below.</div>
      )}
    </div>
  </button>
);

// ---------- Battle ----------
const Battle = ({ player, opp, mode, sfx, onEnd }) => {
  const [pHp, setPHp] = useState(player.stats.hp);
  const [oHp, setOHp] = useState(opp.stats.hp);
  const [log, setLog] = useState([]);
  const [turn, setTurn] = useState("player"); // player | opp
  const [busy, setBusy] = useState(false);
  const [animLeft, setAnimLeft] = useState("idle");
  const [animRight, setAnimRight] = useState("idle");
  const [pop, setPop] = useState({ left: null, right: null });
  const [playerTaunted, setPlayerTaunted] = useState(false);
  const [oppTaunted, setOppTaunted] = useState(false);
  const [playerDefending, setPlayerDefending] = useState(false);
  const [oppDefending, setOppDefending] = useState(false);
  const [healsLeft, setHealsLeft] = useState(2);
  const [specialCd, setSpecialCd] = useState(0);
  const [autoSpeed, setAutoSpeed] = useState(1);
  const [autoRunning, setAutoRunning] = useState(mode === "auto");
  const endedRef = useRef(false);
  const logEndRef = useRef(null);

  const pushLog = useCallback((entry) => {
    setLog((l) => [...l, entry]);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
  }, [log]);

  const popText = (side, text, crit) => {
    setPop((p) => ({ ...p, [side]: { text, crit, id: Math.random() } }));
    setTimeout(() => setPop((p) => ({ ...p, [side]: null })), 1100);
  };

  const setAnim = (side, s) => (side === "left" ? setAnimLeft(s) : setAnimRight(s));

  const applyAttack = useCallback(
    (attackerSide, kind) => {
      const attacker = attackerSide === "left" ? player : opp;
      const defender = attackerSide === "left" ? opp : player;
      const defenderSide = attackerSide === "left" ? "right" : "left";
      const taunted = attackerSide === "left" ? playerTaunted : oppTaunted;
      const defending = defenderSide === "left" ? playerDefending : oppDefending;

      if (kind === "defend") {
        if (attackerSide === "left") setPlayerDefending(true);
        else setOppDefending(true);
        sfx("taunt");
        setAnim(attackerSide, "heal"); // uses the heal/glow bounce as a brace animation
        setTimeout(() => setAnim(attackerSide, "idle"), 500);
        pushLog({
          kind: "defend",
          who: attacker.name,
          text: `${attacker.name} braces for impact — DEFENDING! (-70% dmg next hit)`,
        });
        return;
      }

      if (kind === "taunt") {
        if (attackerSide === "left") setPlayerTaunted(true);
        else setOppTaunted(true);
        sfx("taunt");
        pushLog({
          kind: "taunt",
          who: attacker.name,
          text: `${attacker.name}: "${tauntLine(attacker)}" (+50% next hit)`,
        });
        return;
      }

      if (kind === "heal") {
        const amt = 22 + Math.floor(Math.random() * 10);
        if (attackerSide === "left") {
          setPHp((v) => Math.min(player.stats.hp, v + amt));
        } else {
          setOHp((v) => Math.min(opp.stats.hp, v + amt));
        }
        sfx("heal");
        setAnim(attackerSide, "heal");
        setTimeout(() => setAnim(attackerSide, "idle"), 900);
        popText(attackerSide, `+${amt}`, false);
        pushLog({ kind: "heal", who: attacker.name, text: `${attacker.name} heals +${amt} HP` });
        return;
      }

      // Attack
      let { dmg, crit } = rollDamage(attacker, defender, kind, taunted);
      if (defending) {
        dmg = Math.max(2, Math.round(dmg * 0.3));
      }
      // consume taunt
      if (attackerSide === "left") setPlayerTaunted(false);
      else setOppTaunted(false);
      // consume defend
      if (defenderSide === "left") setPlayerDefending(false);
      else setOppDefending(false);

      if (kind === "special") sfx("special");
      else sfx("punch");

      // attacker lunges
      setAnim(attackerSide, kind === "special" ? "attackSpecial" : "attackPunch");
      // defender reacts a beat later
      setTimeout(() => {
        setAnim(defenderSide, "hurt");
        popText(defenderSide, `-${dmg}${crit ? "!" : ""}${defending ? " BLOCK" : ""}`, crit);
        if (defenderSide === "right") setOHp((v) => v - dmg);
        else setPHp((v) => v - dmg);
      }, kind === "special" ? 350 : 220);
      // reset to idle
      setTimeout(() => setAnim(attackerSide, "idle"), kind === "special" ? 800 : 580);
      setTimeout(() => setAnim(defenderSide, "idle"), kind === "special" ? 950 : 720);

      const verb = kind === "special" ? attacker.specialName : "punches";
      pushLog({
        kind,
        who: attacker.name,
        text: `${attacker.name} ${kind === "special" ? "unleashes " : ""}${verb} on ${defender.name} for ${dmg}${
          crit ? " (CRIT!)" : ""
        }${defending ? " — BLOCKED!" : ""}${mode === "auto" ? " — " + randomCommentary() : ""}`,
      });
    },
    [player, opp, playerTaunted, oppTaunted, playerDefending, oppDefending, pushLog, sfx, mode]
  );

  // detect KO
  useEffect(() => {
    if (endedRef.current) return;
    if (pHp <= 0 || oHp <= 0) {
      endedRef.current = true;
      const playerWon = oHp <= 0 && pHp > 0;
      sfx("ko");
      if (pHp <= 0) setAnimLeft("ko");
      if (oHp <= 0) setAnimRight("ko");
      pushLog({
        kind: "ko",
        who: playerWon ? player.name : opp.name,
        text: `${playerWon ? player.name : opp.name}: "${winLine(playerWon ? player : opp)}" — K.O.!`,
      });
      setTimeout(() => onEnd(playerWon), 1800);
    }
  }, [pHp, oHp, onEnd, player, opp, pushLog, sfx]);

  // OPP AI turn (turn-based mode)
  useEffect(() => {
    if (mode !== "turn") return;
    if (turn !== "opp") return;
    if (endedRef.current) return;
    setBusy(true);
    const t = setTimeout(() => {
      const pct = oHp / opp.stats.hp;
      const r = Math.random();
      let choice = "punch";
      if (pct < 0.3 && r < 0.5) choice = "heal";
      else if (r < 0.2) choice = "defend";
      else if (r < 0.45) choice = "special";
      applyAttack("right", choice);
      setTimeout(() => {
        setBusy(false);
        setTurn("player");
        setSpecialCd((v) => Math.max(0, v - 1));
      }, 950);
    }, 900);
    return () => clearTimeout(t);
  }, [turn, mode, applyAttack, oHp, opp.stats.hp]);

  // AUTO mode loop
  useEffect(() => {
    if (mode !== "auto") return;
    if (!autoRunning) return;
    if (endedRef.current) return;
    const interval = 1100 / autoSpeed;
    let who = "left";
    const t = setInterval(() => {
      if (endedRef.current) return;
      const atk = who;
      const r = Math.random();
      const self = atk === "left" ? pHp : oHp;
      const maxHp = atk === "left" ? player.stats.hp : opp.stats.hp;
      let choice = "punch";
      if (self / maxHp < 0.25 && r < 0.35) choice = "heal";
      else if (r < 0.2) choice = "defend";
      else if (r < 0.45) choice = "special";
      else if (r < 0.55) choice = "taunt";
      applyAttack(atk, choice);
      who = who === "left" ? "right" : "left";
    }, interval);
    return () => clearInterval(t);
  }, [mode, autoRunning, autoSpeed, applyAttack, pHp, oHp, player.stats.hp, opp.stats.hp]);

  const doPlayer = (kind) => {
    if (busy || turn !== "player" || endedRef.current) return;
    if (kind === "special" && specialCd > 0) return;
    if (kind === "heal" && healsLeft <= 0) return;
    if (kind === "special") setSpecialCd(2);
    if (kind === "heal") setHealsLeft((v) => v - 1);
    applyAttack("left", kind);
    setTimeout(() => {
      if (!endedRef.current) setTurn("opp");
    }, 800);
  };

  return (
    <section data-testid="battle-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-2xl sm:text-3xl">
          {mode === "turn" ? "TURN-BASED BRAWL" : "AUTO BATTLER"}
          <span className="font-marker text-sm ml-2 opacity-70">
            {mode === "turn"
              ? turn === "player"
                ? "// your move"
                : "// opponent thinking..."
              : autoRunning
              ? "// in progress"
              : "// paused"}
          </span>
        </h2>
        {mode === "auto" && (
          <div className="flex items-center gap-2" data-testid="auto-controls">
            {[1, 2, 4].map((s) => (
              <button
                key={s}
                onClick={() => setAutoSpeed(s)}
                className={`font-display text-xs px-3 py-2 border-4 border-black cb-shadow-sm ${
                  autoSpeed === s ? "bg-black text-white" : "bg-white"
                }`}
                data-testid={`auto-speed-${s}x`}
              >
                {s}x
              </button>
            ))}
            <button
              onClick={() => setAutoRunning((r) => !r)}
              className="font-display text-xs px-3 py-2 border-4 border-black cb-shadow-sm bg-[#FFDF00]"
              data-testid="auto-pause-btn"
            >
              {autoRunning ? "PAUSE" : "PLAY"}
            </button>
          </div>
        )}
      </div>

      {/* ---------- ARENA STAGE ---------- */}
      <div
        className="relative border-4 border-black rounded-2xl cb-shadow-lg overflow-hidden"
        data-testid="arena-stage"
        style={{ minHeight: 420 }}
      >
        {/* Sky / crowd backdrop */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, #1a1a2e 0%, #2d1b69 40%, #ff2a85 70%, #ffdf00 100%)",
          }}
        />
        {/* Stadium lights */}
        <div className="absolute inset-0 opacity-70 pointer-events-none"
             style={{
               background:
                 "radial-gradient(ellipse at 20% 15%, rgba(255,255,180,0.55) 0%, transparent 25%)," +
                 "radial-gradient(ellipse at 80% 15%, rgba(255,255,180,0.55) 0%, transparent 25%)," +
                 "radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.4) 0%, transparent 20%)",
             }}
        />
        {/* Crowd silhouettes */}
        <div
          className="absolute left-0 right-0 top-[42%] h-[60px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 5% 100%, #000 20px, transparent 22px)," +
              "radial-gradient(circle at 15% 100%, #000 18px, transparent 20px)," +
              "radial-gradient(circle at 25% 100%, #000 22px, transparent 24px)," +
              "radial-gradient(circle at 35% 100%, #000 16px, transparent 18px)," +
              "radial-gradient(circle at 45% 100%, #000 20px, transparent 22px)," +
              "radial-gradient(circle at 55% 100%, #000 22px, transparent 24px)," +
              "radial-gradient(circle at 65% 100%, #000 18px, transparent 20px)," +
              "radial-gradient(circle at 75% 100%, #000 20px, transparent 22px)," +
              "radial-gradient(circle at 85% 100%, #000 16px, transparent 18px)," +
              "radial-gradient(circle at 95% 100%, #000 20px, transparent 22px)",
          }}
        />
        {/* Ring floor */}
        <div
          className="absolute left-0 right-0 bottom-0 h-[52%]"
          style={{
            background:
              "linear-gradient(180deg, #ffdf00 0%, #ff8e3c 60%, #ff2a85 100%)",
            borderTop: "4px solid #000",
          }}
        />
        {/* Floor halftone grain */}
        <div
          className="absolute left-0 right-0 bottom-0 h-[52%] opacity-30 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#000 1px, transparent 2px)",
            backgroundSize: "10px 10px",
          }}
        />
        {/* Perspective floor lines */}
        <svg
          className="absolute left-0 right-0 bottom-0 w-full h-[52%] pointer-events-none"
          viewBox="0 0 100 50"
          preserveAspectRatio="none"
        >
          {[...Array(8)].map((_, i) => (
            <line
              key={i}
              x1={-20 + i * 20}
              y1="0"
              x2={50 + (i - 4) * 60}
              y2="50"
              stroke="rgba(0,0,0,0.25)"
              strokeWidth="0.2"
            />
          ))}
          <line x1="0" y1="12" x2="100" y2="12" stroke="rgba(0,0,0,0.4)" strokeWidth="0.3" />
          <line x1="0" y1="25" x2="100" y2="25" stroke="rgba(0,0,0,0.4)" strokeWidth="0.3" />
          <line x1="0" y1="40" x2="100" y2="40" stroke="rgba(0,0,0,0.4)" strokeWidth="0.3" />
        </svg>

        {/* HP banners */}
        <div className="relative z-10 flex justify-between gap-3 p-3 sm:p-4">
          <ArenaHpBanner
            fighter={player}
            hp={pHp}
            maxHp={player.stats.hp}
            side="left"
            label="YOU"
            defending={playerDefending}
            testId="hp-bar-left"
          />
          <ArenaHpBanner
            fighter={opp}
            hp={oHp}
            maxHp={opp.stats.hp}
            side="right"
            label="RIVAL"
            defending={oppDefending}
            testId="hp-bar-right"
          />
        </div>

        {/* Fighters on stage */}
        <div className="absolute inset-x-0 bottom-0 h-[80%] z-10 flex items-end justify-between px-4 sm:px-10 pb-6">
          <div className="relative flex flex-col items-center" data-testid="arena-side-left">
            <FighterBody fighter={player} facing="right" state={animLeft} size={240} />
            {pop.left && <DamagePop pop={pop.left} />}
            {playerDefending && (
              <div className="absolute -top-2 bg-white border-4 border-black px-2 py-1 font-display text-xs cb-shadow-sm rotate-neg-3">
                <Shield className="inline w-3 h-3 mr-1" /> BLOCKING
              </div>
            )}
          </div>

          <div className="relative mb-10 hidden sm:block">
            <ComicWord word="VS" color="#FFDF00" rotate={-8} />
          </div>

          <div className="relative flex flex-col items-center" data-testid="arena-side-right">
            <FighterBody fighter={opp} facing="left" state={animRight} size={240} />
            {pop.right && <DamagePop pop={pop.right} />}
            {oppDefending && (
              <div className="absolute -top-2 bg-white border-4 border-black px-2 py-1 font-display text-xs cb-shadow-sm rotate-pos-3">
                <Shield className="inline w-3 h-3 mr-1" /> BLOCKING
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Log / commentary */}
      <div
        ref={logEndRef}
        className="mt-5 bg-white border-4 border-black rounded-xl cb-shadow p-3 h-40 overflow-y-auto scroll-hidden"
        data-testid="battle-log"
      >
        {log.length === 0 && (
          <p className="font-marker text-sm opacity-70">
            {mode === "turn" ? "Pick an action to start throwing hands." : "Fight starting..."}
          </p>
        )}
        {log.map((l, i) => (
          <p key={i} className="font-body text-sm mb-1">
            <span className="font-display text-xs bg-black text-white px-2 py-0.5 mr-2">
              {l.kind === "ko"
                ? "KO"
                : l.kind === "special"
                ? "SPECIAL"
                : l.kind === "heal"
                ? "HEAL"
                : l.kind === "taunt"
                ? "TAUNT"
                : l.kind === "defend"
                ? "DEFEND"
                : "HIT"}
            </span>
            {l.text}
          </p>
        ))}
      </div>

      {/* Action dock (turn-based only) */}
      {mode === "turn" && (
        <div
          className="mt-5 bg-white border-4 border-black rounded-2xl cb-shadow-lg p-3 sm:p-4 grid grid-cols-2 sm:grid-cols-4 gap-3"
          data-testid="action-dock"
        >
          <ActionBtn
            onClick={() => doPlayer("punch")}
            disabled={busy || turn !== "player"}
            testId="action-punch"
            label="PUNCH"
            sub="basic hit"
            icon={<Swords className="w-4 h-4" />}
            bg="#FFDF00"
          />
          <ActionBtn
            onClick={() => doPlayer("special")}
            disabled={busy || turn !== "player" || specialCd > 0}
            testId="action-special"
            label={specialCd > 0 ? `SPECIAL (CD ${specialCd})` : "SPECIAL"}
            sub={player.specialName}
            icon={<Zap className="w-4 h-4" />}
            bg="#FF2A85"
            textLight
          />
          <ActionBtn
            onClick={() => doPlayer("defend")}
            disabled={busy || turn !== "player" || playerDefending}
            testId="action-defend"
            label="DEFEND"
            sub="-70% dmg next hit"
            icon={<Shield className="w-4 h-4" />}
            bg="#39FF14"
          />
          <ActionBtn
            onClick={() => doPlayer("heal")}
            disabled={busy || turn !== "player" || healsLeft <= 0}
            testId="action-heal"
            label={`HEAL (${healsLeft})`}
            sub="+22-32 HP"
            icon={<Heart className="w-4 h-4" />}
            bg="#00E5FF"
          />
        </div>
      )}
    </section>
  );
};

// HP banner shown atop the arena stage
const ArenaHpBanner = ({ fighter, hp, maxHp, side, label, defending, testId }) => {
  const pct = Math.max(0, Math.min(100, (hp / maxHp) * 100));
  const color = pct > 55 ? "#00E676" : pct > 25 ? "#FFEA00" : "#FF1744";
  return (
    <div
      className={`flex-1 max-w-[48%] bg-white border-4 border-black rounded-xl cb-shadow-sm p-2 sm:p-3 ${
        side === "left" ? "" : "text-right"
      }`}
      data-testid={testId}
    >
      <div className={`flex items-center gap-2 ${side === "right" ? "flex-row-reverse" : ""}`}>
        <span
          className="font-display text-[10px] bg-black text-white px-2 py-0.5"
          data-testid={`arena-label-${side}`}
        >
          {label}
        </span>
        <span className="font-display text-sm sm:text-base truncate">{fighter.name}</span>
        {defending && (
          <span className="font-display text-[10px] bg-[#39FF14] text-black px-2 py-0.5 border-2 border-black">
            BLOCK
          </span>
        )}
      </div>
      <div className="mt-1 relative h-4 sm:h-5 w-full border-2 border-black bg-neutral-100 overflow-hidden">
        <div
          className="h-full transition-[width] duration-500 ease-out"
          style={{
            width: `${pct}%`,
            background: `repeating-linear-gradient(45deg, ${color} 0 10px, rgba(0,0,0,0.15) 10px 12px, ${color} 12px 22px)`,
          }}
        />
      </div>
      <div className="font-display text-[11px] mt-1">
        {Math.max(0, Math.round(hp))} / {maxHp} HP
      </div>
    </div>
  );
};

const ActionBtn = ({ onClick, disabled, label, sub, icon, bg = "#FFDF00", textLight, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className={`relative border-4 border-black rounded-xl cb-shadow cb-hover p-3 min-h-[60px] text-left ${
      disabled ? "opacity-50 cursor-not-allowed" : ""
    } ${textLight ? "text-white" : "text-black"}`}
    style={{ background: bg }}
  >
    <div className="flex items-center gap-2 font-display text-sm sm:text-base leading-none">
      {icon} {label}
    </div>
    {sub && <div className="font-marker text-[11px] mt-1 opacity-80">{sub}</div>}
  </button>
);

// ---------- Result Screen ----------
const ResultScreen = ({ won, player, opp, onRematch, onNewFighters, onHome }) => (
  <section className="relative min-h-[60vh] flex items-center justify-center" data-testid="result-section">
    <div
      className={`relative w-full max-w-2xl p-6 sm:p-10 border-4 border-black rounded-2xl cb-shadow-lg text-center`}
      style={{ background: won ? "#39FF14" : "#FF2A85" }}
    >
      <div className="halftone-light absolute inset-0 opacity-30 pointer-events-none rounded-2xl" />
      <div className="relative anim-bounce-in">
        <ComicWord word={won ? "VICTORY!" : "K.O.!"} color={won ? "#FFDF00" : "#000"} rotate={-5} size="5xl" />
        <div className="mt-6 flex items-center justify-center gap-6">
          <div className={won ? "" : "opacity-50 grayscale"}>
            <CoinAvatar fighter={player} size={120} />
            <div className="font-display text-sm mt-2">{player.name}</div>
          </div>
          <div className="font-display text-4xl">VS</div>
          <div className={won ? "opacity-50 grayscale" : ""}>
            <CoinAvatar fighter={opp} size={120} />
            <div className="font-display text-sm mt-2">{opp.name}</div>
          </div>
        </div>
        <p className="font-marker text-xl sm:text-2xl mt-5">
          {won
            ? `${player.name}: "${winLine(player)}"`
            : `${opp.name}: "${winLine(opp)}"`}
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={onRematch}
            className="font-display text-lg bg-black text-white hover:bg-neutral-800 border-4 border-black px-6 py-5 cb-shadow cb-hover rounded-xl"
            data-testid="rematch-btn"
          >
            <RotateCcw className="mr-2" /> REMATCH
          </Button>
          <Button
            onClick={onNewFighters}
            className="font-display text-lg bg-white text-black hover:bg-neutral-100 border-4 border-black px-6 py-5 cb-shadow cb-hover rounded-xl"
            data-testid="new-fighters-btn"
          >
            NEW FIGHTERS
          </Button>
          <Button
            onClick={onHome}
            className="font-display text-lg bg-[#FFDF00] text-black hover:bg-[#FFDF00] border-4 border-black px-6 py-5 cb-shadow cb-hover rounded-xl"
            data-testid="result-home-btn"
          >
            HOME
          </Button>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Scorecard Dialog ----------
const ScorecardDialog = ({ open, onOpenChange, sc, onReset }) => {
  const fav = favoriteFighter(sc);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#FFDF00] border-4 border-black cb-shadow-lg rounded-2xl max-w-xl"
        data-testid="scorecard-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">SCORECARD</DialogTitle>
          <DialogDescription className="font-marker text-base text-black/80">
            Session stats, saved locally. No wallet needed.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <StatPip label="WINS" value={sc.wins} />
          <StatPip label="LOSSES" value={sc.losses} />
          <StatPip label="STREAK" value={sc.streak} />
          <StatPip label="BEST" value={sc.bestStreak} />
        </div>

        <div className="mt-4 bg-white border-4 border-black p-3 rounded-xl">
          <div className="font-display text-sm mb-2">PER-FIGHTER</div>
          <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto scroll-hidden">
            {FIGHTERS.map((f) => {
              const row = sc.perFighter[f.id] || { wins: 0, losses: 0 };
              return (
                <div
                  key={f.id}
                  className="flex items-center gap-2 border-2 border-black p-2 rounded"
                  style={{ background: f.color }}
                  data-testid={`scorecard-row-${f.id}`}
                >
                  <CoinAvatar fighter={f} size={36} />
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-xs leading-none">{f.name}</div>
                    <div className="font-body text-xs">
                      {row.wins}W / {row.losses}L
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="font-marker text-sm">
            {fav ? (
              <>
                MOST PLAYED: <strong>{fav.name}</strong>
              </>
            ) : (
              "No fights yet — go punch something."
            )}
          </div>
          <button
            onClick={onReset}
            className="font-display text-xs bg-white border-4 border-black px-3 py-2 cb-shadow-sm cb-hover"
            data-testid="scorecard-reset-btn"
          >
            RESET
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
